import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeaveRequestsPageFull({ token, user }) {
  const { language } = useLanguage();
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('my');
  const [showApply, setShowApply] = useState(false);
  const [form, setForm] = useState({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });

  const isAdmin = user?.role === 'school_admin' || user?.role === 'platform_admin';

  useEffect(() => { if (token) loadData(); }, [token]);

  const api = async (endpoint, method = 'GET', body = null) => {
    const opts = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
    if (method !== 'GET') { opts.method = method; if (body) opts.body = JSON.stringify(body); }
    const res = await fetch(`${API_URL}${endpoint}`, opts);
    return res.json();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const mine = await api('/api/leave/my-requests');
      setMyRequests(mine.requests || []);
    } catch (err) { console.error('leave requests error:', err); }
    try {
      const tmpls = await api('/api/leave/templates');
      setTemplates(tmpls.templates || []);
    } catch (err) { console.error('templates error:', err); }
    if (isAdmin) {
      try {
        const pending = await api('/api/leave/pending-approvals?approval_level=admin');
        setPendingApprovals(pending.requests || []);
      } catch (e) { console.error('pending error:', e); }
    }
    setLoading(false);
  };

  const applyLeave = async () => {
    await api('/api/leave/request', 'POST', form);
    setShowApply(false);
    setForm({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
    loadData();
  };

  const approveLeave = async (requestId, status) => {
    await api('/api/leave/approve', 'POST', { request_id: requestId, status, remarks: '' });
    loadData();
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="leave-requests-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {language === 'hi' ? 'छुट्टी प्रबंधन / Leave Management' : 'Leave Management'}
          </h1>
          <p className="text-sm text-gray-500">{language === 'hi' ? 'छुट्टी apply करें, status track करें' : 'Apply for leave, track status'}</p>
        </div>
        <button onClick={() => setShowApply(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600" data-testid="apply-leave-btn">
          + {language === 'hi' ? 'छुट्टी Apply' : 'Apply Leave'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit">
        <button onClick={() => setTab('my')} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'my' ? 'bg-white shadow text-orange-700' : 'text-gray-500'}`} data-testid="tab-my">
          <i className="fa-solid fa-user mr-1"></i> {language === 'hi' ? 'मेरी छुट्टियाँ' : 'My Leaves'}
        </button>
        {isAdmin && (
          <button onClick={() => setTab('approve')} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'approve' ? 'bg-white shadow text-orange-700' : 'text-gray-500'}`} data-testid="tab-approve">
            <i className="fa-solid fa-check-circle mr-1"></i> {language === 'hi' ? 'Approve करें' : 'Approvals'}
            {pendingApprovals.length > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-1.5 rounded-full">{pendingApprovals.length}</span>}
          </button>
        )}
      </div>

      {/* My Requests */}
      {tab === 'my' && (
        <div className="space-y-3">
          {myRequests.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <i className="fa-solid fa-calendar-check text-4xl text-gray-300 mb-3 block"></i>
              <p className="text-gray-400">{language === 'hi' ? 'कोई छुट्टी request नहीं' : 'No leave requests'}</p>
            </div>
          ) : myRequests.map((req, i) => (
            <div key={req.id || i} className="bg-white rounded-xl p-4 border shadow-sm" data-testid={`leave-request-${i}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm capitalize">{req.leave_type} Leave</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{req.start_date} → {req.end_date}</p>
                  {req.reason && <p className="text-xs text-gray-500 mt-1">{req.reason}</p>}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[req.status] || 'bg-gray-100'}`}>
                  {req.status === 'pending' ? (language === 'hi' ? 'Pending' : 'Pending') : req.status === 'approved' ? (language === 'hi' ? 'Approved' : 'Approved') : (language === 'hi' ? 'Rejected' : 'Rejected')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending Approvals (Admin) */}
      {tab === 'approve' && isAdmin && (
        <div className="space-y-3">
          {pendingApprovals.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <i className="fa-solid fa-check-double text-4xl text-green-300 mb-3 block"></i>
              <p className="text-gray-400">{language === 'hi' ? 'कोई pending request नहीं' : 'No pending requests'}</p>
            </div>
          ) : pendingApprovals.map((req, i) => (
            <div key={req.id || i} className="bg-white rounded-xl p-4 border shadow-sm" data-testid={`approval-${i}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">{req.applicant_name || 'Staff'} — {req.leave_type} Leave</h3>
                  <p className="text-xs text-gray-400">{req.start_date} → {req.end_date}</p>
                  {req.reason && <p className="text-xs text-gray-500 mt-1">{req.reason}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approveLeave(req.id, 'approved')} className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600" data-testid={`approve-btn-${i}`}>
                    <i className="fa-solid fa-check mr-1"></i>{language === 'hi' ? 'Approve' : 'Approve'}
                  </button>
                  <button onClick={() => approveLeave(req.id, 'rejected')} className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs hover:bg-red-200" data-testid={`reject-btn-${i}`}>
                    <i className="fa-solid fa-times mr-1"></i>{language === 'hi' ? 'Reject' : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Apply Leave Modal */}
      {showApply && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full" data-testid="apply-leave-modal">
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="font-bold">{language === 'hi' ? 'छुट्टी Apply करें' : 'Apply for Leave'}</h2>
              <button onClick={() => setShowApply(false)} className="text-2xl text-gray-400">&times;</button>
            </div>
            <div className="p-5 space-y-3">
              <select value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" data-testid="leave-type-select">
                <option value="casual">{language === 'hi' ? 'Casual Leave' : 'Casual Leave'}</option>
                <option value="sick">{language === 'hi' ? 'Sick Leave' : 'Sick Leave'}</option>
                <option value="earned">{language === 'hi' ? 'Earned Leave' : 'Earned Leave'}</option>
                <option value="maternity">{language === 'hi' ? 'Maternity Leave' : 'Maternity Leave'}</option>
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
                  className="px-3 py-2 border rounded-lg" data-testid="leave-start" />
                <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                  className="px-3 py-2 border rounded-lg" data-testid="leave-end" />
              </div>
              <textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg h-20" placeholder={language === 'hi' ? 'कारण लिखें...' : 'Reason...'} data-testid="leave-reason" />
            </div>
            <div className="p-5 border-t flex gap-3">
              <button onClick={() => setShowApply(false)} className="flex-1 py-2 border rounded-lg">Cancel</button>
              <button onClick={applyLeave} disabled={!form.start_date || !form.end_date}
                className="flex-1 py-2 bg-orange-500 text-white rounded-lg font-medium disabled:opacity-40" data-testid="submit-leave-btn">
                {language === 'hi' ? 'Apply करें' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
